<script setup lang="ts">
import { VPercentageField, VTextField, VToggleButtonField } from '@/modules/shared/components'
import VSelectLocal from '../../../components/v-select.vue'

const props = defineProps<{
  form: any
  v$: any
  capitalCallFrequencyOptions: { label: string; value: string }[]
  ownershipTypeOptions: { label: string; value: string }[]
}>()

const ownershipTypeDescriptions: Record<string, string> = {
  committed: 'Ownership will be calculated based on the commitment to the fund',
  capital_called:
    'Ownership in the fund and its assets is calculated by the capital called. This takes into account the fees paid, which might be different for each investor.',
  assigned: 'Allows you to manually assign an ownership % to each investor',
}

defineExpose({
  isValid: async () => {
    const fields = ['management_fee_percentage', 'carried_interest', 'preferred_return']
    const results = await Promise.all(fields.filter((f) => props.v$[f]).map((f) => props.v$[f].$validate()))
    return results.every(Boolean)
  },
})
</script>

<template>
  <div class="space-y-6">
    <div class="grid gap-4 md:grid-cols-2">
      <VPercentageField
        v-model="form.management_fee_percentage"
        label="Management Fee %"
        :isPrecalculated="true"
        property="management_fee_percentage"
        :v$="v$"
      />
      <VPercentageField
        v-model="form.carried_interest"
        label="Carried Interest %"
        :isPrecalculated="true"
        property="carried_interest"
        :v$="v$"
      />
    </div>
    <div class="grid gap-4 md:grid-cols-2">
      <VPercentageField
        v-model="form.preferred_return"
        label="Preferred Return %"
        :isPrecalculated="true"
        property="preferred_return"
        :v$="v$"
      />
    </div>

    <VSelectLocal
      v-model="form.capital_call_frequency"
      label="Capital call frequency"
      :options="capitalCallFrequencyOptions"
      property="capital_call_frequency"
      :v$="v$"
    />

    <VSelectLocal
      v-model="form.ownership_type"
      label="How would you like to allocate the portfolio to investors?"
      :options="ownershipTypeOptions"
      :description="ownershipTypeDescriptions[form.ownership_type]"
      property="ownership_type"
      :v$="v$"
    />

    <div class="grid gap-4 md:grid-cols-2">
      <VTextField
        v-model="form.initial_closing_date"
        type="date"
        label="Initial closing date"
        property="initial_closing_date"
        :v$="v$"
      />
      <VTextField
        v-model="form.investment_period_end_date"
        type="date"
        label="Investment period end date"
        property="investment_period_end_date"
        :v$="v$"
      />
    </div>

    <fieldset class="max-w-full rounded-3xl border border-slate-200/80 bg-white p-5 shadow-sm">
      <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p class="text-sm font-semibold text-slate-800">Waterfall method</p>
          <p class="text-xs text-slate-500">Choose how carry is released to the GP across exits.</p>
        </div>
        <span class="rounded-full bg-[#3b88af]/10 px-3 py-1 text-xs font-semibold text-[#3b88af]">
          Structured for funds
        </span>
      </div>

      <div class="mt-4 grid gap-4 md:grid-cols-2">
        <button
          type="button"
          class="group flex h-full flex-col gap-3 rounded-2xl border p-4 text-left transition"
          :class="
            form.waterfall_calculation === 'american'
              ? 'border-[#3b88af] bg-[#3b88af]/10 shadow-sm ring-1 ring-[#3b88af]/15'
              : 'border-slate-200 hover:border-[#3b88af]/50 hover:bg-[#3b88af]/10'
          "
          @click="form.waterfall_calculation = 'american'"
        >
          <div class="flex items-center justify-between">
            <span class="text-base font-semibold text-slate-900">American</span>
            <span
              class="rounded-full px-3 py-1 text-[11px] font-semibold"
              :class="
                form.waterfall_calculation === 'american'
                  ? 'bg-white text-[#3b88af] shadow-sm'
                  : 'bg-slate-100 text-slate-600'
              "
            >
              Deal-by-deal
            </span>
          </div>
          <p class="text-sm text-slate-600">
            American waterfall is calculated on a deal-by-deal basis, and a GP is compensated for each successful deal.
            This often allows the GP to begin taking a share of the profits, or carried interest, earlier in the life of
            a fund.
          </p>
        </button>

        <button
          type="button"
          class="group flex h-full flex-col gap-3 rounded-2xl border p-4 text-left transition"
          :class="
            form.waterfall_calculation === 'european'
              ? 'border-[#3b88af] bg-[#3b88af]/10 shadow-sm ring-1 ring-[#3b88af]/15'
              : 'border-slate-200 hover:border-[#3b88af]/50 hover:bg-[#3b88af]/10'
          "
          @click="form.waterfall_calculation = 'european'"
        >
          <div class="flex items-center justify-between">
            <span class="text-base font-semibold text-slate-900">European</span>
            <span
              class="rounded-full px-3 py-1 text-[11px] font-semibold"
              :class="
                form.waterfall_calculation === 'european'
                  ? 'bg-white text-[#3b88af] shadow-sm'
                  : 'bg-slate-100 text-slate-600'
              "
            >
              Fund-level
            </span>
          </div>
          <p class="text-sm text-slate-600">
            In a European waterfall, 100% of the contributed capital and preferred return is paid out to investors on a
            pro rata basis before the GP receives any distribution of carried interest. Because it's pro rata, all
            capital is treated equally, and distributions are paid out in proportion to the amount of capital invested.
          </p>
        </button>
      </div>
    </fieldset>

    <VToggleButtonField
      v-model="form.has_interest_catch_up_provision"
      label="Does this fund have an interest catch-up provision?"
      property="has_interest_catch_up_provision"
      :v$="v$"
    />
  </div>
</template>
